import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { useForm, useFieldArray } from 'react-hook-form';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  BarChart3, 
  Users, 
  ChevronDown, 
  ChevronUp,
  Check,
  X,
  Eye,
  Clock,
  Search,
  MapPin,
  ExternalLink,
  Archive,
  ArchiveRestore,
  GripVertical,
  ClipboardCopy
} from 'lucide-react';
import { 
  useAdminPolls, 
  useCreatePoll, 
  useUpdatePoll, 
  useDeletePoll,
  usePreviewPollTarget,
  useSearchCities
} from '@/lib/api';
import { PollAPI, PollTargetCriteria } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import Markdown from '@/components/Markdown';
import { MarkdownTextarea } from '../components/MarkdownTextarea';
import { useConfirm } from '@/components/ConfirmModal';
import { PollAnswerDetailsFields } from '../components/PollAnswerDetailsFields';
import { emptyPollAnswer, fromPollAnswerForm, toPollAnswerForm, type PollAnswerFormValue } from '../utils/pollAnswers';
import { Loader } from '@/components/Loader';

// Sortable Answer Row Component
interface SortableAnswerRowProps {
  id: string;
  index: number;
  register: any;
  canDelete: boolean;
  onDelete: () => void;
  value: PollAnswerFormValue;
  onImageChange: (url: string) => void;
}

function SortableAnswerRow({ id, index, register, canDelete, onDelete, value, onImageChange }: SortableAnswerRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-1">
    <div className="flex gap-2 items-center">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-dark-muted hover:text-primary-500 transition-colors"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <input
        {...register(`answers.${index}.text`, { required: 'Answer text is required' })}
        className="input flex-1"
        placeholder={`Answer ${index + 1}`}
      />
      {canDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="btn btn-ghost text-red-400 px-3"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
    <PollAnswerDetailsFields index={index} register={register} value={value} onImageChange={onImageChange} />
    </div>
  );
}

interface PollFormData {
  title: string;
  question: string;
  answers: PollAnswerFormValue[];
  publicResults: boolean;
  status: 'draft' | 'active' | 'closed' | 'archived';
  startsAt: string;
  endsAt: string;
  // Target criteria
  subscribersOnly: boolean;
  notSubscribed: boolean;
  hasSpentMoney: boolean;
  cities: string[];
  quizAnswerRegex: { field: string; pattern: string }[];
}

export default function AdminPolls() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPoll, setEditingPoll] = useState<PollAPI.PollWithResults | null>(null);
  const [expandedPoll, setExpandedPoll] = useState<string | null>(null);
  const [targetPreviewCount, setTargetPreviewCount] = useState<number | null>(null);
  const [showCitySearch, setShowCitySearch] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [newCityId, setNewCityId] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedResultCities, setSelectedResultCities] = useState<Record<string, Set<string>>>({});

  const { data: pollsData, isLoading } = useAdminPolls();
  const { data: citiesData } = useSearchCities(citySearchQuery);
  const createPoll = useCreatePoll();
  const updatePoll = useUpdatePoll();
  const deletePoll = useDeletePoll();
  const previewTarget = usePreviewPollTarget();
  const toast = useToast();
  const { confirm } = useConfirm();

  const cities = citiesData?.data || [];
  const allPolls = pollsData?.data || [];
  const polls = showArchived ? allPolls : allPolls.filter(p => p.status !== 'archived');
  const archivedCount = allPolls.filter(p => p.status === 'archived').length;

  // Helper to toggle city selection for poll results
  const toggleResultCity = (pollId: string, cityId: string) => {
    setSelectedResultCities(prev => {
      const pollCities = prev[pollId] || new Set<string>();
      const newCities = new Set(pollCities);
      if (newCities.has(cityId)) {
        newCities.delete(cityId);
      } else {
        newCities.add(cityId);
      }
      return { ...prev, [pollId]: newCities };
    });
  };

  // Get selected cities for a poll (defaults to all cities if none selected)
  const getSelectedCitiesForPoll = (pollId: string, availableCities: string[]): string[] => {
    const selected = selectedResultCities[pollId];
    if (!selected || selected.size === 0) {
      return availableCities; // Show all by default
    }
    return [...selected];
  };

  // Calculate stacked results for selected cities
  const getStackedResults = (poll: PollAPI.PollWithResults) => {
    if (!poll.resultsByCity || poll.resultsByCity.length === 0) {
      return null;
    }

    const availableCities = poll.resultsByCity.map(c => c.cityId);
    const selectedCities = getSelectedCitiesForPoll(poll.id, availableCities);
    const filteredCityResults = poll.resultsByCity.filter(c => selectedCities.includes(c.cityId));
    
    // Calculate totals across selected cities
    const totalResponsesSelected = filteredCityResults.reduce((sum, c) => sum + c.totalResponses, 0);
    
    // For each answer, get the stacked counts per city
    const stackedData = poll.answers.map(answer => {
      const cityBreakdown = filteredCityResults.map(cityResult => {
        const result = cityResult.results.find(r => r.answerId === answer.id);
        return {
          cityId: cityResult.cityId,
          cityName: cityResult.cityName,
          count: result?.count || 0,
        };
      });
      
      const totalCount = cityBreakdown.reduce((sum, c) => sum + c.count, 0);
      
      return {
        answerId: answer.id,
        answerText: answer.text,
        totalCount,
        percentage: totalResponsesSelected > 0 ? (totalCount / totalResponsesSelected) * 100 : 0,
        cityBreakdown,
      };
    });

    return {
      stackedData,
      totalResponses: totalResponsesSelected,
      allCities: poll.resultsByCity,
      selectedCities,
    };
  };

  // Generate a consistent color for each city
  const getCityColor = (_cityId: string, index: number) => {
    const colors = [
      'bg-primary-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-cyan-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    return colors[index % colors.length];
  };

  const { register, handleSubmit, reset, watch, setValue, control, formState: { errors } } = useForm<PollFormData>({
    defaultValues: {
      title: '',
      question: '',
      answers: [emptyPollAnswer(), emptyPollAnswer()],
      publicResults: false,
      status: 'draft',
      startsAt: '',
      endsAt: '',
      subscribersOnly: false,
      notSubscribed: false,
      hasSpentMoney: false,
      cities: [],
      quizAnswerRegex: [],
    },
  });

  const { fields: answerFields, append: appendAnswer, remove: removeAnswer, move: moveAnswer } = useFieldArray({
    control,
    name: 'answers',
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = answerFields.findIndex((field) => field.id === active.id);
      const newIndex = answerFields.findIndex((field) => field.id === over.id);
      moveAnswer(oldIndex, newIndex);
    }
  };

  const { fields: regexFields, append: appendRegex, remove: removeRegex } = useFieldArray({
    control,
    name: 'quizAnswerRegex',
  });

  const watchedCriteria = watch(['subscribersOnly', 'notSubscribed', 'hasSpentMoney', 'cities', 'quizAnswerRegex']);

  // Preview target count when criteria changes
  useEffect(() => {
    const criteria: PollTargetCriteria = {};
    const [subscribersOnly, notSubscribed, hasSpentMoney, selectedCities, quizRegex] = watchedCriteria;
    
    if (subscribersOnly) criteria.subscribersOnly = true;
    if (notSubscribed) criteria.notSubscribed = true;
    if (hasSpentMoney) criteria.hasSpentMoney = true;
    if (selectedCities && selectedCities.length > 0) criteria.cities = selectedCities;
    if (quizRegex && quizRegex.length > 0 && quizRegex.some(r => r.field && r.pattern)) {
      criteria.quizAnswerRegex = quizRegex.filter(r => r.field && r.pattern);
    }

    // Always call preview - empty criteria returns all users
    previewTarget.mutate(criteria, {
      onSuccess: (data) => {
        setTargetPreviewCount(data.data?.count ?? null);
      },
    });
  }, [JSON.stringify(watchedCriteria)]);

  const openEditForm = (poll: PollAPI.PollWithResults) => {
    setEditingPoll(poll);
    reset({
      title: poll.title,
      question: poll.question,
      answers: poll.answers.map(toPollAnswerForm),
      publicResults: poll.publicResults,
      status: poll.status,
      startsAt: poll.startsAt ? new Date(poll.startsAt).toISOString().slice(0, 16) : '',
      endsAt: poll.endsAt ? new Date(poll.endsAt).toISOString().slice(0, 16) : '',
      subscribersOnly: poll.targetCriteria?.subscribersOnly || false,
      notSubscribed: poll.targetCriteria?.notSubscribed || false,
      hasSpentMoney: poll.targetCriteria?.hasSpentMoney || false,
      cities: poll.targetCriteria?.cities || [],
      quizAnswerRegex: poll.targetCriteria?.quizAnswerRegex || [],
    });
    setShowCreateForm(true);
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingPoll(null);
    reset({
      title: '',
      question: '',
      answers: [emptyPollAnswer(), emptyPollAnswer()],
      publicResults: false,
      status: 'draft',
      startsAt: '',
      endsAt: '',
      subscribersOnly: false,
      notSubscribed: false,
      hasSpentMoney: false,
      cities: [],
      quizAnswerRegex: [],
    });
    setTargetPreviewCount(null);
  };


  const onSubmit = async (data: PollFormData) => {
    // Build target criteria
    const targetCriteria: PollTargetCriteria = {};
    if (data.subscribersOnly) targetCriteria.subscribersOnly = true;
    if (data.notSubscribed) targetCriteria.notSubscribed = true;
    if (data.hasSpentMoney) targetCriteria.hasSpentMoney = true;
    if (data.cities && data.cities.length > 0) targetCriteria.cities = data.cities;
    if (data.quizAnswerRegex && data.quizAnswerRegex.length > 0) {
      const validRegex = data.quizAnswerRegex.filter(r => r.field && r.pattern);
      if (validRegex.length > 0) targetCriteria.quizAnswerRegex = validRegex;
    }

    const pollData = {
      title: data.title,
      question: data.question,
      answers: data.answers.filter(a => a.text.trim()).map(a => ({ ...fromPollAnswerForm(a), id: undefined })),
      publicResults: data.publicResults,
      targetCriteria: Object.keys(targetCriteria).length > 0 ? targetCriteria : null,
      startsAt: data.startsAt || null,
      endsAt: data.endsAt || null,
    };

    if (editingPoll) {
      updatePoll.mutate(
        { 
          id: editingPoll.id, 
          data: { 
            ...pollData, 
            status: data.status,
            // Each row carries its answer id, so reordering keeps votes attached.
            answers: data.answers.filter(a => a.text.trim()).map(fromPollAnswerForm),
          } 
        },
        {
          onSuccess: () => {
            toast.success('Poll updated successfully');
            closeForm();
          },
          onError: (error: any) => {
            toast.error(error?.response?.data?.error || 'Failed to update poll');
          },
        }
      );
    } else {
      createPoll.mutate(pollData, {
        onSuccess: () => {
          toast.success('Poll created successfully');
          closeForm();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to create poll');
        },
      });
    }
  };

  const handleDelete = async (poll: PollAPI.PollWithResults) => {
    const confirmed = await confirm({
      title: 'Delete Poll',
      message: `Are you sure you want to delete "${poll.title}"? This will also delete all responses.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
    });

    if (confirmed) {
      deletePoll.mutate(poll.id, {
        onSuccess: () => {
          toast.success('Poll deleted successfully');
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to delete poll');
        },
      });
    }
  };

  const handleStatusChange = async (poll: PollAPI.PollWithResults, newStatus: 'draft' | 'active' | 'closed' | 'archived') => {
    updatePoll.mutate(
      { id: poll.id, data: { status: newStatus } },
      {
        onSuccess: () => {
          toast.success(`Poll ${newStatus === 'active' ? 'activated' : newStatus === 'closed' ? 'closed' : newStatus === 'archived' ? 'archived' : 'set to draft'}`);
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.error || 'Failed to update poll status');
        },
      }
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-400">Active</span>;
      case 'closed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-500/20 text-gray-400">Closed</span>;
      case 'archived':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-500/20 text-purple-400">Archived</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-400">Draft</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center pt-16">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8 flex-col gap-2 md:flex-row"
      >
        <div className="flex-1">
          <h1 className="text-4xl font-bold mb-2">Poll Management</h1>
          <p className="text-dark-muted">Create and manage community polls</p>
        </div>
        <div className="flex gap-2 items-center">
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`btn btn-ghost text-sm flex items-center gap-2 ${showArchived ? 'text-purple-400' : ''}`}
            >
              <Archive className="w-4 h-4" />
              {showArchived ? 'Hide' : 'Show'} Archived ({archivedCount})
            </button>
          )}
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary w-auto"
          >
            <Plus className="w-5 h-5 mr-2 inline-block" />
            Create Poll
          </button>
        </div>
      </motion.div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="card mb-8"
        >
          <h2 className="text-2xl font-bold mb-6">
            {editingPoll ? 'Edit Poll' : 'Create New Poll'}
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Title</label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  className="input"
                  placeholder="Poll title (internal reference)"
                />
                {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title.message}</p>}
              </div>
              {editingPoll && (
                <div>
                  <label className="label">Status</label>
                  <select {...register('status')} className="input">
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              )}
            </div>

            <div>
              <label className="label">Question</label>
              <MarkdownTextarea
                registration={register('question', { required: 'Question is required' })}
                value={watch('question')}
                onChange={v => setValue('question', v, { shouldDirty: true, shouldValidate: true })}
                placeholder="What would you like to ask?"
              />
              {errors.question && <p className="text-red-400 text-sm mt-1">{errors.question.message}</p>}
            </div>

            {/* Answers */}
            <div>
              <label className="label">Answers (drag to reorder)</label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={answerFields.map(f => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {answerFields.map((field, index) => (
                      <SortableAnswerRow
                        key={field.id}
                        id={field.id}
                        index={index}
                        register={register}
                        canDelete={answerFields.length > 2}
                        onDelete={() => removeAnswer(index)}
                        value={watch(`answers.${index}`)}
                        onImageChange={(url) => setValue(`answers.${index}.imageUrl`, url, { shouldDirty: true })}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <button
                type="button"
                onClick={() => appendAnswer(emptyPollAnswer())}
                className="btn btn-ghost mt-2 text-sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Answer
              </button>
            </div>

            {/* Timing */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Start Date (optional)</label>
                <input
                  {...register('startsAt')}
                  type="datetime-local"
                  className="input"
                />
              </div>
              <div>
                <label className="label">End Date (optional)</label>
                <input
                  {...register('endsAt')}
                  type="datetime-local"
                  className="input"
                />
              </div>
            </div>

            {/* Settings */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('publicResults')}
                  className="w-4 h-4 rounded border-dark-border bg-dark-card"
                />
                <span className="text-sm">Show results publicly</span>
              </label>
            </div>

            {/* Target Criteria */}
            <div className="border border-dark-border rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Target Audience
                {targetPreviewCount !== null && (
                  <span className="ml-2 text-sm font-normal text-primary-400">
                    ({targetPreviewCount} users match)
                  </span>
                )}
              </h3>

              <div className="space-y-4">
                {/* Subscription Status */}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('subscribersOnly')}
                      className="w-4 h-4 rounded border-dark-border bg-dark-card"
                    />
                    <span className="text-sm">Subscribers only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('notSubscribed')}
                      className="w-4 h-4 rounded border-dark-border bg-dark-card"
                    />
                    <span className="text-sm">Non-subscribers only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('hasSpentMoney')}
                      className="w-4 h-4 rounded border-dark-border bg-dark-card"
                    />
                    <span className="text-sm">Has spent money</span>
                  </label>
                </div>

                {/* Cities */}
                <div>
                  <label className="label text-sm">Filter by Cities</label>
                  
                  {/* Quick select buttons */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setValue('cities', [])}
                      className={`px-3 py-1 rounded-full text-sm cursor-pointer border transition-colors ${
                        !watch('cities')?.length
                          ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                          : 'border-dark-border hover:border-primary-500/50'
                      }`}
                    >
                      🌍 All Cities
                    </button>
                    {cities.filter(c => c.available).map((city) => (
                      <button
                        key={city.id}
                        type="button"
                        onClick={() => {
                          const current = watch('cities') || [];
                          if (current.includes(city.id)) {
                            setValue('cities', current.filter(c => c !== city.id));
                          } else {
                            setValue('cities', [...current, city.id]);
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-sm cursor-pointer border transition-colors ${
                          watch('cities')?.includes(city.id)
                            ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                            : 'border-dark-border hover:border-primary-500/50'
                        }`}
                      >
                        {city.emoji} {city.name}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setShowCitySearch(true)}
                      className="px-3 py-1 rounded-full text-sm cursor-pointer border border-dashed border-dark-border hover:border-primary-500/50 flex items-center gap-1"
                    >
                      <Search className="w-3 h-3" /> Search more
                    </button>
                  </div>

                  {/* Selected cities as editable list */}
                  {watch('cities')?.length > 0 && (
                    <div className="mb-3">
                      <label className="text-xs text-dark-muted mb-1 block">Selected city IDs (click to remove):</label>
                      <div className="flex flex-wrap gap-1">
                        {watch('cities').map((cityId) => {
                          const cityInfo = cities.find(c => c.id === cityId);
                          return (
                            <button
                              key={cityId}
                              type="button"
                              onClick={() => {
                                const current = watch('cities') || [];
                                setValue('cities', current.filter(c => c !== cityId));
                              }}
                              className="px-2 py-0.5 rounded bg-primary-500/20 text-primary-400 text-xs flex items-center gap-1 hover:bg-red-500/20 hover:text-red-400 transition-colors"
                              title={`Remove ${cityId}`}
                            >
                              {cityInfo ? `${cityInfo.emoji} ${cityInfo.name}` : cityId}
                              <X className="w-3 h-3" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Add city ID directly */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCityId}
                      onChange={(e) => setNewCityId(e.target.value)}
                      placeholder="Add city ID directly (e.g. usa_nyc)"
                      className="input flex-1 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newCityId.trim()) {
                          e.preventDefault();
                          const current = watch('cities') || [];
                          if (!current.includes(newCityId.trim())) {
                            setValue('cities', [...current, newCityId.trim()]);
                          }
                          setNewCityId('');
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (newCityId.trim()) {
                          const current = watch('cities') || [];
                          if (!current.includes(newCityId.trim())) {
                            setValue('cities', [...current, newCityId.trim()]);
                          }
                          setNewCityId('');
                        }
                      }}
                      className="btn btn-ghost text-sm"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Quiz Answer Regex */}
                <div>
                  <label className="label text-sm">Filter by Quiz Answers (regex)</label>
                  <div className="space-y-2">
                    {regexFields.map((field, index) => (
                      <div key={field.id} className="flex gap-2">
                        <input
                          {...register(`quizAnswerRegex.${index}.field`)}
                          className="input flex-1"
                          placeholder="Field name (e.g. industry)"
                        />
                        <input
                          {...register(`quizAnswerRegex.${index}.pattern`)}
                          className="input flex-1"
                          placeholder="Regex pattern (e.g. tech|software)"
                        />
                        <button
                          type="button"
                          onClick={() => removeRegex(index)}
                          className="btn btn-ghost text-red-400 px-3"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => appendRegex({ field: '', pattern: '' })}
                    className="btn btn-ghost mt-2 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Filter
                  </button>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-4">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={createPoll.isPending || updatePoll.isPending}
              >
                {(createPoll.isPending || updatePoll.isPending) ? 'Saving...' : (editingPoll ? 'Update Poll' : 'Create Poll')}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="btn btn-ghost"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Polls List */}
      <div className="space-y-4">
        {polls.length === 0 ? (
          <div className="card text-center py-12">
            <BarChart3 className="w-16 h-16 text-dark-muted mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No polls yet</h3>
            <p className="text-dark-muted">Create your first poll to gather community feedback</p>
          </div>
        ) : (
          polls.map((poll, index) => (
            <motion.div
              key={poll.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="card"
            >
              {/* Poll Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Link 
                      href={`/polls/${poll.id}`} 
                      className="text-xl font-bold hover:text-primary-400 transition-colors flex items-center gap-2 group"
                    >
                      {poll.title}
                      <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                    {getStatusBadge(poll.status)}
                    {poll.publicResults && (
                      <span className="flex items-center gap-1 text-xs text-dark-muted">
                        <Eye className="w-3 h-3" /> Public
                      </span>
                    )}
                  </div>
                  <Markdown size="base" className="text-dark-muted mb-2">{poll.question}</Markdown>
                  <div className="flex items-center gap-4 text-sm text-dark-muted">
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {poll.totalResponses} {poll.totalResponses === 1 ? 'response' : 'responses'}
                    </span>
                    {poll.startsAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Starts: {new Date(poll.startsAt).toLocaleDateString()}
                      </span>
                    )}
                    {poll.endsAt && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Ends: {new Date(poll.endsAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                      onClick={() => {
                        navigator.clipboard.writeText(`https://app.outclose.com/poll/${poll.id}`);
                        toast.success('Poll link copied to clipboard');
                      }}
                      className="btn btn-ghost p-2"
                    >
                    <ClipboardCopy className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setExpandedPoll(expandedPoll === poll.id ? null : poll.id)}
                    className="btn btn-ghost p-2"
                  >
                    {expandedPoll === poll.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => openEditForm(poll)}
                    className="btn btn-ghost p-2"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(poll)}
                    className="btn btn-ghost p-2 text-red-400"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Quick Status Actions */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {poll.status !== 'active' && poll.status !== 'archived' && (
                  <button
                    onClick={() => handleStatusChange(poll, 'active')}
                    className="btn btn-ghost text-sm text-green-400"
                  >
                    <Check className="w-4 h-4 mr-1" /> Activate
                  </button>
                )}
                {poll.status === 'active' && (
                  <button
                    onClick={() => handleStatusChange(poll, 'closed')}
                    className="btn btn-ghost text-sm text-yellow-400"
                  >
                    <X className="w-4 h-4 mr-1" /> Close
                  </button>
                )}
                {poll.status === 'closed' && (
                  <button
                    onClick={() => handleStatusChange(poll, 'draft')}
                    className="btn btn-ghost text-sm"
                  >
                    Reset to Draft
                  </button>
                )}
                {poll.status !== 'archived' && (
                  <button
                    onClick={() => handleStatusChange(poll, 'archived')}
                    className="btn btn-ghost text-sm text-purple-400"
                  >
                    <Archive className="w-4 h-4 mr-1" /> Archive
                  </button>
                )}
                {poll.status === 'archived' && (
                  <button
                    onClick={() => handleStatusChange(poll, 'draft')}
                    className="btn btn-ghost text-sm text-purple-400"
                  >
                    <ArchiveRestore className="w-4 h-4 mr-1" /> Unarchive
                  </button>
                )}
              </div>

              {/* Expanded Results */}
              {expandedPoll === poll.id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="border-t border-dark-border pt-4 mt-4"
                >
                  <h4 className="font-semibold mb-4">Results</h4>
                  
                  {/* City Selection for Results */}
                  {poll.resultsByCity && poll.resultsByCity.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-sm text-dark-muted mr-2">Filter by city:</span>
                        <button
                          onClick={() => setSelectedResultCities(prev => ({ ...prev, [poll.id]: new Set<string>() }))}
                          className={`px-3 py-1 rounded-full text-xs cursor-pointer border transition-colors ${
                            !selectedResultCities[poll.id] || selectedResultCities[poll.id].size === 0
                              ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                              : 'border-dark-border hover:border-primary-500/50'
                          }`}
                        >
                          All Cities
                        </button>
                        {poll.resultsByCity.map((cityResult, idx) => {
                          const isSelected = selectedResultCities[poll.id]?.has(cityResult.cityId);
                          const colorClass = getCityColor(cityResult.cityId, idx);
                          return (
                            <button
                              key={cityResult.cityId}
                              onClick={() => toggleResultCity(poll.id, cityResult.cityId)}
                              className={`px-3 py-1 rounded-full text-xs cursor-pointer border transition-colors flex items-center gap-1.5 ${
                                isSelected
                                  ? 'bg-primary-500/20 border-primary-500 text-primary-400'
                                  : 'border-dark-border hover:border-primary-500/50'
                              }`}
                            >
                              <span className={`w-2 h-2 rounded-full ${colorClass}`}></span>
                              {cityResult.cityName} ({cityResult.totalResponses})
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Stacked Results */}
                  {(() => {
                    const stacked = getStackedResults(poll);
                    if (stacked && stacked.allCities.length > 0) {
                      return (
                        <div className="space-y-3">
                          {stacked.stackedData.map((data) => (
                            <div key={data.answerId}>
                              <div className="flex justify-between text-sm mb-1">
                                <span>{data.answerText}</span>
                                <span className="text-dark-muted">
                                  {data.totalCount} ({data.percentage.toFixed(1)}%)
                                </span>
                              </div>
                              <div className="h-4 bg-dark-border rounded-full overflow-hidden flex">
                                {data.cityBreakdown.map((city, idx) => {
                                  const widthPercent = stacked.totalResponses > 0 
                                    ? (city.count / stacked.totalResponses) * 100 
                                    : 0;
                                  if (widthPercent === 0) return null;
                                  const colorClass = getCityColor(city.cityId, stacked.allCities.findIndex(c => c.cityId === city.cityId));
                                  return (
                                    <motion.div
                                      key={city.cityId}
                                      initial={{ width: 0 }}
                                      animate={{ width: `${widthPercent}%` }}
                                      transition={{ duration: 0.5, delay: 0.1 + idx * 0.05 }}
                                      className={`h-full ${colorClass} first:rounded-l-full last:rounded-r-full`}
                                      title={`${city.cityName}: ${city.count}`}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                          
                          {/* Legend */}
                          <div className="flex flex-wrap gap-3 mt-4 text-xs text-dark-muted">
                            {stacked.allCities
                              .filter(c => stacked.selectedCities.includes(c.cityId))
                              .map((city) => {
                                const colorClass = getCityColor(city.cityId, stacked.allCities.findIndex(c => c.cityId === city.cityId));
                                return (
                                  <div key={city.cityId} className="flex items-center gap-1.5">
                                    <span className={`w-3 h-3 rounded-sm ${colorClass}`}></span>
                                    <span>{city.cityName}</span>
                                    <span className="text-dark-muted/60">({city.totalResponses})</span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      );
                    }
                    
                    // Fallback to simple results if no city data
                    return (
                      <div className="space-y-3">
                        {poll.results.map((result) => (
                          <div key={result.answerId}>
                            <div className="flex justify-between text-sm mb-1">
                              <span>{result.answerText}</span>
                              <span className="text-dark-muted">
                                {result.count} ({result.percentage.toFixed(1)}%)
                              </span>
                            </div>
                            <div className="h-2 bg-dark-border rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${result.percentage}%` }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="h-full bg-primary-500 rounded-full"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Target Criteria Display */}
                  {poll.targetCriteria && Object.keys(poll.targetCriteria).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-dark-border">
                      <h4 className="font-semibold mb-2 text-sm">Target Criteria</h4>
                      <div className="flex flex-wrap gap-2">
                        {poll.targetCriteria.subscribersOnly && (
                          <span className="px-2 py-1 text-xs rounded bg-primary-500/20">Subscribers only</span>
                        )}
                        {poll.targetCriteria.notSubscribed && (
                          <span className="px-2 py-1 text-xs rounded bg-primary-500/20">Non-subscribers</span>
                        )}
                        {poll.targetCriteria.hasSpentMoney && (
                          <span className="px-2 py-1 text-xs rounded bg-primary-500/20">Has spent money</span>
                        )}
                        {poll.targetCriteria.cities?.map(cityId => (
                          <span key={cityId} className="px-2 py-1 text-xs rounded bg-primary-500/20">
                            {cities.find(c => c.id === cityId)?.name || cityId}
                          </span>
                        ))}
                        {poll.targetCriteria.quizAnswerRegex?.map((r, i) => (
                          <span key={i} className="px-2 py-1 text-xs rounded bg-primary-500/20">
                            {r.field}: /{r.pattern}/
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* City Search Modal */}
      {showCitySearch && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => {
            setShowCitySearch(false);
            setCitySearchQuery('');
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card border border-content rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Search Cities
              </h2>
              <button
                onClick={() => {
                  setShowCitySearch(false);
                  setCitySearchQuery('');
                }}
                className="text-dark-muted hover:text-primary-500 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted" />
                <input
                  type="text"
                  value={citySearchQuery}
                  onChange={(e) => setCitySearchQuery(e.target.value)}
                  placeholder="Search for a city..."
                  className="input pl-10 w-full"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-1">
              {cities.length === 0 ? (
                <p className="text-dark-muted text-center py-8">No cities found</p>
              ) : (
                cities.map((city) => {
                  const isSelected = watch('cities')?.includes(city.id);
                  return (
                    <button
                      key={city.id}
                      type="button"
                      onClick={() => {
                        const current = watch('cities') || [];
                        if (isSelected) {
                          setValue('cities', current.filter(c => c !== city.id));
                        } else {
                          setValue('cities', [...current, city.id]);
                        }
                      }}
                      className={`w-full px-3 py-2 rounded-lg text-left flex items-center justify-between transition-colors ${
                        isSelected
                          ? 'bg-primary-500/20 border border-primary-500'
                          : 'hover:bg-dark-card border border-transparent'
                      }`}
                    >
                      <div>
                        <span className="mr-2">{city.emoji}</span>
                        <span className="font-medium">{city.name}</span>
                        <span className="text-xs text-dark-muted ml-2">({city.id})</span>
                        {!city.available && (
                          <span className="text-xs text-yellow-400 ml-2">(not available)</span>
                        )}
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-primary-500" />}
                    </button>
                  );
                })
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-dark-border">
              <button
                type="button"
                onClick={() => {
                  setShowCitySearch(false);
                  setCitySearchQuery('');
                }}
                className="btn btn-primary w-full"
              >
                Done
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

